import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  setNativeStatusBarStyle,
  type NativeStatusBarStyle,
} from '@/utils/nativeBridge';

/* ============================================================
   STATUS BAR SCOPE

   The native shell draws the status bar over whatever we paint,
   and only it can colour the glyphs — iOS gives a WKWebView no say
   in that, and `<meta name="theme-color">` does nothing there. But
   the shell cannot see which screen it is showing, so it holds one
   style for the whole app, and one style cannot serve screens that
   are mostly light with a couple that are black. The symptom was
   white glyphs left over from the splash: fine on My Game, and
   invisible against Home, Profile and Shop.

   So we tell it. This watches the route and posts the style for
   the screen being entered. The style names the GLYPHS, not the
   background — 'light' is white glyphs, for a dark screen.

   WHY A WATCHER AND NOT A HOOK PER SCREEN. Same reasoning as
   BookingLocationScope next door: a screen is arrived at by tab
   tap, by `navigate(..., { replace: true })` after a booking, by
   the system back gesture, and by deep link, and a per-screen
   hook has to be remembered on all of them plus every screen
   added later. Watching where the member IS covers every route in,
   including ones nobody has written yet — an unlisted route gets
   DEFAULT, which is right for any screen built on `--tb-bg`.

   Mounted once, next to the router's other invisible listeners.
   ============================================================ */

/** What an unlisted route gets: dark glyphs, for `--tb-bg`. */
const DEFAULT: NativeStatusBarStyle = 'dark';

/* Screens whose top edge is dark, so the glyphs over them must be
   white. Both are full-bleed brand video on `--tb-black`: Welcome
   hands over from the native splash, and My Game pins the same
   footage behind its copy. Everything else in v2 is a light
   surface and takes DEFAULT.

   Prefixes, not exact matches, so a child route inherits its
   parent's treatment. */
const DARK_SCREENS = [
  '/welcome',
  '/my-game',
  /* The scanner is black to the top edge. Notifications, Tee Buddies,
     Settings and Tee Time are NOT listed: `.tb-app` reserves a cream strip
     above their forest title bars, so white glyphs there were invisible. */
  '/community/scan',
];

export function StatusBarScope() {
  const { pathname } = useLocation();

  useEffect(() => {
    const dark = DARK_SCREENS.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`),
    );
    setNativeStatusBarStyle(dark ? 'light' : DEFAULT);
  }, [pathname]);

  return null;
}
